import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { monthRange, downloadCsv, downloadPdf, toastExportNeedsLoad } from '../reportUtils';
import { useTenantCompanyName, ReportToolbar, ReportDocument, fmtRp } from '../ReportShell';

export default function ReportCashFlow() {
  const company = useTenantCompanyName();
  const { start: mStart, end: mEnd } = monthRange();
  const [cfStart, setCfStart] = useState(mStart);
  const [cfEnd, setCfEnd] = useState(mEnd);
  const [cf, setCf] = useState(null);

  const loadCf = () => {
    api.get('/tenant/accounting/reports/cash-flow', { params: { start: cfStart, end: cfEnd } }).then((r) => setCf(r.data)).catch(() => toast.error('Gagal arus kas'));
  };

  return (
    <div>
      <ReportToolbar>
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-slate-600">Dari</Label>
          <Input type="date" value={cfStart} onChange={(e) => setCfStart(e.target.value)} className="h-9 w-[150px]" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-slate-600">Sampai</Label>
          <Input type="date" value={cfEnd} onChange={(e) => setCfEnd(e.target.value)} className="h-9 w-[150px]" />
        </div>
        <Button type="button" onClick={loadCf} className="h-9">
          Muat data
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9"
          onClick={() => {
            if (!cf) return toastExportNeedsLoad();
            downloadCsv('/tenant/accounting/reports/cash-flow', { start: cfStart, end: cfEnd }, 'arus_kas.csv');
          }}
        >
          <Download className="mr-1 h-4 w-4" />
          CSV
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9"
          onClick={() => {
            if (!cf) return toastExportNeedsLoad();
            downloadPdf('/tenant/accounting/reports/cash-flow', { start: cfStart, end: cfEnd }, 'arus_kas.pdf');
          }}
        >
          PDF
        </Button>
      </ReportToolbar>

      {cf && (
        <ReportDocument
          companyName={company}
          title="RINGKASAN ARUS KAS (KAS & BANK)"
          subtitle="Perubahan saldo akun kas dan rekening bank"
          periodLabel={`Periode ${cfStart} s.d. ${cfEnd}`}
        >
          <div className="acct-rpt-summary-bar mb-6">
            <div className="grid gap-2 sm:grid-cols-3">
              <div>
                <span className="text-slate-500">Saldo awal gabungan</span>
                <p className="text-base font-bold text-slate-900">{fmtRp(cf.opening_cash)}</p>
              </div>
              <div>
                <span className="text-slate-500">Saldo akhir gabungan</span>
                <p className="text-base font-bold text-slate-900">{fmtRp(cf.closing_cash)}</p>
              </div>
              <div>
                <span className="text-slate-500">Perubahan bersih</span>
                <p className={`text-base font-bold ${cf.net_change >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtRp(cf.net_change)}</p>
              </div>
            </div>
          </div>

          <p className="acct-rpt-section-label mb-2">Rincian per akun</p>
          <table className="acct-rpt-table">
            <thead>
              <tr>
                <th>Kode</th>
                <th>Nama akun</th>
                <th className="acct-rpt-th-num">Mutasi bersih</th>
              </tr>
            </thead>
            <tbody>
              {(cf.accounts?.length ? cf.accounts : []).map((a) => (
                <tr key={a.account_id}>
                  <td className="acct-rpt-code">{a.code}</td>
                  <td>{a.name}</td>
                  <td className={`acct-rpt-num font-medium ${a.net_change >= 0 ? '' : 'text-red-700'}`}>{fmtRp(a.net_change)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportDocument>
      )}

      {!cf && <div className="acct-rpt-empty">Pilih periode lalu klik <strong>Muat data</strong>.</div>}
    </div>
  );
}
